import { useQuery } from '@tanstack/react-query'

import type {
  AllListings200Response,
  AllListingsListing,
} from '@b-stock/search-api-client'

import { fetchData } from './api/fetchAuctionData'
import { parseCustomDate } from './dateUtils'

const ONE_MINUTE = 60 * 1000 // 1 minute in milliseconds
const TEN_MINUTES = 10 * ONE_MINUTE
const THIRTY_MINUTES = 30 * ONE_MINUTE

// Filter out expired auctions that are within 10 minutes of their end time
function filterExpiredAuctions(auctions: AllListingsListing[]) {
  const now = Date.now()
  return auctions.filter((auction) => {
    const endTimeMs = parseCustomDate(auction.endTime).getTime()
    return endTimeMs - now >= TEN_MINUTES
  })
}

// Deduplicate auctions by auctionId when there are multiple auction queries
// being merged together.
function mergeUniqueAuctions(
  auctionSets: AllListingsListing[][]
): AllListingsListing[] {
  // If there's only one auction set, we already know there are no duplicates
  if (auctionSets.length === 1) {
    // Length check guarantees index 0 exists
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return auctionSets[0]!
  }

  // Length check above guarantees index 0 exists
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const uniqueAuctions = [...auctionSets[0]!]
  const uniqueListingIds = new Set(
    uniqueAuctions.map((auction) => auction.listingId)
  )
  for (let i = 1; i < auctionSets.length; i++) {
    // Loop condition guarantees index i exists (i < length)
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    for (const auction of auctionSets[i]!) {
      if (!uniqueListingIds.has(auction.listingId)) {
        uniqueAuctions.push(auction)
        uniqueListingIds.add(auction.listingId)
      }
    }
  }

  return uniqueAuctions
}

// Generates a query object for fetching auctions which can be passed into
// useQuery, useSuspenseQuery, or prefetchQuery
export function auctionQuery(endpoint: string | string[]) {
  return {
    queryKey: ['auctions', endpoint],
    queryFn: async () => {
      const endpoints = Array.isArray(endpoint) ? endpoint : [endpoint]
      const queryPromises = endpoints.map((endpoint) =>
        fetchData<AllListings200Response>(endpoint).then((data) => {
          return filterExpiredAuctions(data.listings)
        })
      )
      const auctionSets = await Promise.all(queryPromises)
      return mergeUniqueAuctions(auctionSets)
    },
    // staleTime matches refetchInterval so re-mounting the carousel within the
    // 30-minute window (e.g. navigating away and back) serves cached data
    // instead of triggering an immediate refetch.
    staleTime: THIRTY_MINUTES,
    gcTime: THIRTY_MINUTES,
    refetchInterval: THIRTY_MINUTES,
    refetchIntervalInBackground: false,
  }
}

export function useAuctionQuery(endpoint: string | string[]) {
  return useQuery(auctionQuery(endpoint))
}
